import axios from "axios";
import apiClient from "./client";

// Types
export interface PublicCampaign {
  uuid: string;
  name: string;
  type: "form" | "access" | "hidden";
  displayMode: "step" | "all";
  thankYouMessage: string | null;
  ctaUrl: string | null;
  ctaText: string | null;
  questions: PublicCampaignQuestion[];
}

export interface PublicCampaignQuestion {
  id: number;
  type: "text" | "single_choice" | "multiple_choice";
  text: string;
  options: string[] | null;
  required: boolean;
  order: number;
}

export interface QuestionnaireResponse {
  questionId: number;
  value: string | null;
}

export interface CampaignSubmitResult {
  code: string;
  assigned: boolean;
  thankYouMessage: string | null;
  ctaUrl: string | null;
  ctaText: string | null;
}

export interface VoucherValidation {
  valid: boolean;
  code: string;
  campaign: {
    name: string;
    discountConfig: {
      type: string;
      plans?: {
        standard?: { discount: number; unit: string };
        premium?: { discount: number; unit: string };
      };
    };
  } | null;
  discount: { discount: number; unit: string } | null;
}

export interface UserVoucher {
  id: number;
  code: string;
  assignedAt: string;
  usedAt: string | null;
  used: boolean;
  canBeUsed: boolean;
  discountApplied: unknown | null;
  campaign: {
    name: string;
    discountConfig: unknown;
  } | null;
}

// Get access token from localStorage
function getAccessToken(): string {
  return localStorage.getItem("access_token") || "";
}

/**
 * Get public campaign info
 */
export async function getPublicCampaign(uuid: string): Promise<PublicCampaign> {
  const { data } = await axios.get<PublicCampaign>(`/api/campaigns/${uuid}`);
  return data;
}

/**
 * Submit questionnaire for a form-based campaign
 */
export async function submitQuestionnaire(
  uuid: string,
  responses: QuestionnaireResponse[]
): Promise<CampaignSubmitResult> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Include auth token if available
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const { data } = await axios.post<CampaignSubmitResult>(
    `/api/campaigns/${uuid}/submit`,
    { responses },
    { headers }
  );
  return data;
}

/**
 * Claim a voucher from an access-based campaign
 */
export async function claimCampaignVoucher(uuid: string): Promise<CampaignSubmitResult> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Include auth token if available
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const { data } = await axios.post<CampaignSubmitResult>(
    `/api/campaigns/${uuid}/claim`,
    {},
    { headers }
  );
  return data;
}

/**
 * Validate a voucher code
 */
export async function validateVoucherCode(
  code: string,
  plan?: "standard" | "premium"
): Promise<VoucherValidation> {
  // Sent through the authenticated client on purpose (JUB-27): a claimed
  // single-use code is only valid for the account that holds it, and the
  // backend answers per-account when it knows who is asking.
  const { data } = await apiClient.post<VoucherValidation>("/vouchers/validate", {
    code,
    plan,
  });
  return data;
}

/**
 * Stable machine-readable reasons the backend attaches to a rejected voucher
 * (`error.errors.reason`); mapped to `payment.discount.errors.<reason>`.
 */
export const VOUCHER_REJECTION_REASONS = [
  "not_found",
  "inactive",
  "expired",
  "exhausted",
  "already_used",
  "not_applicable",
  "campaign_already_claimed",
] as const;

export type VoucherRejectionReason = (typeof VOUCHER_REJECTION_REASONS)[number];

/** Extract the rejection reason from a failed voucher/payment request, if any. */
export function getVoucherRejectionReason(error: unknown): VoucherRejectionReason | null {
  const reason = (error as { response?: { data?: { error?: { errors?: { reason?: unknown } } } } })
    ?.response?.data?.error?.errors?.reason;
  return typeof reason === "string" && (VOUCHER_REJECTION_REASONS as readonly string[]).includes(reason)
    ? (reason as VoucherRejectionReason)
    : null;
}

/** Translation key for the user-facing message of a failed voucher check. */
export function voucherErrorKey(error: unknown): string {
  const reason = getVoucherRejectionReason(error);
  return `payment.discount.errors.${reason ?? "generic"}`;
}

/**
 * Get user's vouchers (requires auth)
 */
export async function getUserVouchers(): Promise<{ vouchers: UserVoucher[] }> {
  const { data } = await axios.get("/api/user/vouchers", {
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });
  return data;
}

/**
 * Get user's available vouchers (requires auth)
 */
export async function getAvailableVouchers(): Promise<{ vouchers: UserVoucher[] }> {
  const { data } = await axios.get("/api/user/vouchers/available", {
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });
  return data;
}

/**
 * Assign a pending voucher to the current user (requires auth)
 */
export async function assignPendingVoucher(
  code: string
): Promise<{ message: string; voucher: UserVoucher }> {
  const { data } = await axios.post(
    "/api/campaigns/assign-pending",
    { code },
    {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`,
      },
    }
  );
  return data;
}

// LocalStorage helpers for guest voucher storage
const PENDING_VOUCHER_KEY = "jubiel:pending_voucher";

export interface PendingVoucher {
  campaignUuid: string;
  code: string;
  claimedAt: string;
}

/**
 * Store a pending voucher in localStorage (for guests)
 */
export function storePendingVoucher(campaignUuid: string, code: string): void {
  const pendingVoucher: PendingVoucher = {
    campaignUuid,
    code,
    claimedAt: new Date().toISOString(),
  };
  localStorage.setItem(PENDING_VOUCHER_KEY, JSON.stringify(pendingVoucher));
}

/**
 * Get pending voucher from localStorage
 */
export function getPendingVoucher(): PendingVoucher | null {
  const stored = localStorage.getItem(PENDING_VOUCHER_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

/**
 * Clear pending voucher from localStorage
 */
export function clearPendingVoucher(): void {
  localStorage.removeItem(PENDING_VOUCHER_KEY);
}

/**
 * Check URL for voucher parameter
 */
export function getVoucherFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("voucher");
}
